// src/reports/ProfitLossReport.js
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

/* ----------------- helpers ----------------- */
const dkey = (d) =>
  !d ? "" : typeof d === "string" ? d.slice(0, 10) : format(new Date(d), "yyyy-MM-dd");
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function getOnlineSumsFromZ(z) {
  // explicit columns
  let onlineCard = num(z.snappy_card) + num(z.justeat_card) + num(z.deliveroo_card);
  let onlineCash = num(z.snappy_cash) + num(z.justeat_cash) + num(z.deliveroo_cash);

  // JSONB extension
  const partners = z?.online_partners || {};
  if (partners && typeof partners === "object") {
    for (const [k, v] of Object.entries(partners)) {
      const key = k.toLowerCase();
      if (key.endsWith("_card")) onlineCard += num(v);
      if (key.endsWith("_cash")) onlineCash += num(v);
    }
  }
  return { onlineCard, onlineCash };
}

/* --------------- table names ---------------- */
const TBL = {
  ZREPORTS: "zreports",
  CLOCKINS: "clockins",
  EXPENSES: "expenses",
  WASTAGE: "wastage",
  STOCKTAKE: "stocktake",
};

export default function ProfitLossReport({ fromDate, toDate, hourlyRate: propRate }) {
  const [hourlyRate, setHourlyRate] = useState(
    typeof propRate === "number" && propRate > 0 ? propRate : 9.5
  );
  const [viewType, setViewType] = useState("daily"); // daily | weekly | monthly
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fromDate && toDate) fetchData();
  }, [fromDate, toDate, viewType, hourlyRate]);

  async function fetchData() {
    setLoading(true);
    try {
      const [
        { data: zreports, error: ez },
        { data: clockins, error: ec },
        { data: expenses, error: ee },
        { data: wastage, error: ew },
        { data: stocktakes, error: es },
      ] = await Promise.all([
        supabase.from(TBL.ZREPORTS).select("*").gte("date", fromDate).lte("date", toDate),
        supabase.from(TBL.CLOCKINS).select("*").gte("date", fromDate).lte("date", toDate),
        supabase.from(TBL.EXPENSES).select("*").gte("date", fromDate).lte("date", toDate),
        supabase.from(TBL.WASTAGE).select("*").gte("date", fromDate).lte("date", toDate),
        supabase.from(TBL.STOCKTAKE).select("*").gte("date", fromDate).lte("date", toDate),
      ]);

      if (ez || ec || ee || ew || es) {
        const msg = [ez, ec, ee, ew, es].filter(Boolean).map((e) => e.message).join(" · ");
        throw new Error(msg || "Fetch failed");
      }

      // union of all dates present anywhere
      const dateSet = new Set();
      (zreports || []).forEach((r) => dateSet.add(dkey(r.date)));
      (clockins || []).forEach((r) => dateSet.add(dkey(r.date)));
      (expenses || []).forEach((r) => dateSet.add(dkey(r.date)));
      (wastage || []).forEach((r) => dateSet.add(dkey(r.date)));
      (stocktakes || []).forEach((r) => dateSet.add(dkey(r.date)));

      // ZReport per-day aggregation
      const zMap = {};
      (zreports || []).forEach((z) => {
        const d = dkey(z.date);
        if (!zMap[d]) zMap[d] = [];
        zMap[d].push(z);
      });

      // Clock-ins per day
      const clkMap = {};
      (clockins || []).forEach((c) => {
        const d = dkey(c.date);
        if (!clkMap[d]) clkMap[d] = { hours: 0, fee: 0 };
        clkMap[d].hours += num(c.total_hours_decimal);
        clkMap[d].fee += num(c.delivery_fee);
      });

      // Expenses per day
      const paidStaffMap = {};
      const otherExpMap = {};
      (expenses || []).forEach((e) => {
        const d = dkey(e.date);
        const amt = num(e.amount);
        if ((e.category || "").toLowerCase() === "paid to staff") {
          paidStaffMap[d] = (paidStaffMap[d] || 0) + amt;
        } else {
          otherExpMap[d] = (otherExpMap[d] || 0) + amt;
        }
      });

      // Wastage per day
      const wastMap = {};
      (wastage || []).forEach((w) => {
        const d = dkey(w.date);
        wastMap[d] = (wastMap[d] || 0) + num(w.amount);
      });

      // Stocktake variance per day
      const stockVarMap = {};
      (stocktakes || []).forEach((s) => {
        const d = dkey(s.date);
        stockVarMap[d] = (stockVarMap[d] || 0) + num(s.variance);
      });

      // build rows for every date in the union
      const allDates = Array.from(dateSet).filter(Boolean).sort();
      let out = allDates.map((d) => {
        // aggregate ZReport
        const zrows = zMap[d] || [];
        let oscCard = 0,
          oscCash = 0,
          zFee = 0,
          varCash = 0,
          supCash = 0,
          supBank = 0,
          onlineCard = 0,
          onlineCash = 0;

        for (const zr of zrows) {
          oscCard += num(zr.one_stop_card);
          oscCash += num(zr.one_stop_cash);
          const o = getOnlineSumsFromZ(zr);
          onlineCard += o.onlineCard;
          onlineCash += o.onlineCash;
          zFee += num(zr.delivery_fee);
          varCash += num(zr.variance);
          supCash += num(zr.supplier_cash_out);
          supBank += num(zr.supplier_bank_out);
        }

        // other sources
        const { hours = 0, fee: feeClk = 0 } = clkMap[d] || {};
        const paidToStaff = paidStaffMap[d] || 0;
        const otherExp = otherExpMap[d] || 0;
        const wastage = wastMap[d] || 0;
        const stockVar = stockVarMap[d] || 0;

        // Net wages = hours×rate + clockin fee − paid via expense
        const netWages = hours * hourlyRate + feeClk - paidToStaff;

        // P&L — NOTE: add varCash as signed (±)
        const netProfit =
          0.22 * (oscCard + oscCash) +
          0.11 * (onlineCard + onlineCash) +
          zFee -
          netWages -
          wastage -
          stockVar -
          supCash -
          supBank -
          otherExp +
          varCash; // variance is ±, so add it

        return {
          date: d,
          // revenue
          oscCard,
          oscCash,
          onlineCard,
          onlineCash,
          zFee,
          // adjustments
          varCash,
          // costs
          netWages,
          wastage,
          stockVar,
          supCash,
          supBank,
          otherExp,
          // info
          feeClk,
          hours,
          hrRate: hourlyRate,
          paidToStaff,
          // summary
          netProfit,
        };
      });

      // group (weekly / monthly)
      if (viewType !== "daily") {
        const g = {};
        for (const r of out) {
          const key = viewType === "weekly" ? format(parseISO(r.date), "RRRR-'W'II") : r.date.slice(0, 7);
          if (!g[key]) g[key] = { ...r, date: key };
          else {
            for (const [k, v] of Object.entries(r)) {
              if (k === "date") continue;
              g[key][k] = (g[key][k] || 0) + (typeof v === "number" ? v : 0);
            }
          }
          g[key].hrRate = hourlyRate;
        }
        out = Object.values(g);
      }

      setRows(out);
    } catch (err) {
      console.error("Profit/Loss fetch error:", err);
      toast.error("Data fetch error: " + err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  /* ----------------- CSV ----------------- */
  const exportCSV = () => {
    if (!rows.length) return;
    const header = [
      "Date",
      "OneStop Card (22%)",
      "OneStop Cash (22%)",
      "Online Card (11%)",
      "Online Cash (11%)",
      "Z Delivery Fee",
      "Cash Variance (±)",
      "Net Wages (hrs×rate + clk fee − paid)",
      "Wastage",
      "Stock Variance",
      "Supplier Cash",
      "Supplier Bank",
      "Other Expenses",
      "Clock-in Fee",
      "Hours",
      "Hr Rate",
      "Paid via Expense",
      "Net Profit",
    ];
    const lines = [
      header.join(","),
      ...rows.map((r) =>
        [
          r.date,
          r.oscCard.toFixed(2),
          r.oscCash.toFixed(2),
          r.onlineCard.toFixed(2),
          r.onlineCash.toFixed(2),
          r.zFee.toFixed(2),
          r.varCash.toFixed(2),
          r.netWages.toFixed(2),
          r.wastage.toFixed(2),
          r.stockVar.toFixed(2),
          r.supCash.toFixed(2),
          r.supBank.toFixed(2),
          r.otherExp.toFixed(2),
          r.feeClk.toFixed(2),
          r.hours.toFixed(2),
          num(r.hrRate).toFixed(2),
          r.paidToStaff.toFixed(2),
          r.netProfit.toFixed(2),
        ].join(",")
      ),
    ];
    saveAs(new Blob([lines.join("\n")], { type: "text/csv" }), `profit_loss_${fromDate}_${toDate}.csv`);
  };

  /* ----------------- totals ----------------- */
  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      for (const [k, v] of Object.entries(r)) {
        if (k === "date") continue;
        if (typeof v === "number") acc[k] = (acc[k] || 0) + v;
      }
      acc.hrRate = hourlyRate;
      return acc;
    }, {});
  }, [rows, hourlyRate]);

  /* ----------------- styles ----------------- */
  const s = {
    groupRevenue: { background: "#E8F5E9", color: "#1B5E20" },   // light green
    groupAdj: { background: "#FFF8E1", color: "#8D6E63" },      // light amber
    groupCosts: { background: "#FFEBEE", color: "#B71C1C" },    // light red
    groupInfo: { background: "#E3F2FD", color: "#0D47A1" },     // light blue
    groupSummary: { background: "#ECEFF1", color: "#37474F" },  // grey
    tfoot: { background: "#F5F5F5", fontWeight: "bold" },
    tdRevenue: { color: "#1B5E20" },
    tdCosts: { color: "#B71C1C" },
    tdAdj: { color: "#8D6E63" },
    tdInfo: { color: "#0D47A1" },
  };

  /* ----------------- UI ----------------- */
  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => setViewType("daily")} disabled={loading}>Daily</button>
        <button onClick={() => setViewType("weekly")} disabled={loading}>Weekly</button>
        <button onClick={() => setViewType("monthly")} disabled={loading}>Monthly</button>
        <button onClick={exportCSV} disabled={!rows.length} style={{ marginLeft: 8 }}>Export CSV</button>

        <div style={{ marginLeft: 16 }}>
          <label style={{ marginRight: 6 }}>Hourly Rate:</label>
          <input
            type="number"
            step="0.01"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(num(e.target.value))}
            style={{ width: 90 }}
          />
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : !rows.length ? (
        <p>No data for the selected range.</p>
      ) : (
        <table>
          <thead>
            {/* Group row */}
            <tr>
              <th rowSpan="2">Date</th>
              <th colSpan="5" style={s.groupRevenue}>Revenue</th>
              <th colSpan="1" style={s.groupAdj}>Adjustments</th>
              <th colSpan="6" style={s.groupCosts}>Costs</th>
              <th colSpan="4" style={s.groupInfo}>Inputs / Context</th>
              <th rowSpan="2" style={s.groupSummary}>Net Profit</th>
            </tr>
            {/* Labels row */}
            <tr>
              <th>OneStop Card (22%)</th>
              <th>OneStop Cash (22%)</th>
              <th>Online Card (11%)</th>
              <th>Online Cash (11%)</th>
              <th>Z Delivery Fee</th>

              <th>Cash Variance (±)</th>

              <th>Net Wages (hrs×rate + clk fee − paid)</th>
              <th>Wastage</th>
              <th>Stock Variance</th>
              <th>Supplier Cash</th>
              <th>Supplier Bank</th>
              <th>Other Expenses</th>

              <th>Clock-in Fee</th>
              <th>Hours</th>
              <th>Hr Rate</th>
              <th>Paid via Expense</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>

                <td style={s.tdRevenue}>{r.oscCard.toFixed(2)}</td>
                <td style={s.tdRevenue}>{r.oscCash.toFixed(2)}</td>
                <td style={s.tdRevenue}>{r.onlineCard.toFixed(2)}</td>
                <td style={s.tdRevenue}>{r.onlineCash.toFixed(2)}</td>
                <td style={s.tdRevenue}>{r.zFee.toFixed(2)}</td>

                <td style={s.tdAdj}>{r.varCash.toFixed(2)}</td>

                <td style={s.tdCosts}>{r.netWages.toFixed(2)}</td>
                <td style={s.tdCosts}>{r.wastage.toFixed(2)}</td>
                <td style={s.tdCosts}>{r.stockVar.toFixed(2)}</td>
                <td style={s.tdCosts}>{r.supCash.toFixed(2)}</td>
                <td style={s.tdCosts}>{r.supBank.toFixed(2)}</td>
                <td style={s.tdCosts}>{r.otherExp.toFixed(2)}</td>

                <td style={s.tdInfo}>{r.feeClk.toFixed(2)}</td>
                <td style={s.tdInfo}>{r.hours.toFixed(2)}</td>
                <td style={s.tdInfo}>{num(r.hrRate).toFixed(2)}</td>
                <td style={s.tdInfo}>{r.paidToStaff.toFixed(2)}</td>

                <td style={{ color: r.netProfit < 0 ? "crimson" : "green", fontWeight: 600 }}>
                  {r.netProfit.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={s.tfoot}>
              <th>Totals</th>
              <th>{num(totals.oscCard).toFixed(2)}</th>
              <th>{num(totals.oscCash).toFixed(2)}</th>
              <th>{num(totals.onlineCard).toFixed(2)}</th>
              <th>{num(totals.onlineCash).toFixed(2)}</th>
              <th>{num(totals.zFee).toFixed(2)}</th>

              <th>{num(totals.varCash).toFixed(2)}</th>

              <th>{num(totals.netWages).toFixed(2)}</th>
              <th>{num(totals.wastage).toFixed(2)}</th>
              <th>{num(totals.stockVar).toFixed(2)}</th>
              <th>{num(totals.supCash).toFixed(2)}</th>
              <th>{num(totals.supBank).toFixed(2)}</th>
              <th>{num(totals.otherExp).toFixed(2)}</th>

              <th>{num(totals.feeClk).toFixed(2)}</th>
              <th>{num(totals.hours).toFixed(2)}</th>
              <th>{num(totals.hrRate).toFixed(2)}</th>
              <th>{num(totals.paidToStaff).toFixed(2)}</th>

              <th style={{ color: num(totals.netProfit) < 0 ? "crimson" : "green" }}>
                {num(totals.netProfit).toFixed(2)}
              </th>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
